import React, { useEffect } from 'react';
import { Tooltip } from '@mui/material';
import { useInvoiceSummary } from '../../hooks/useInvoiceSummary';
import './../../scss/components/stat-card.style.scss';
import { useTranslation } from 'react-i18next';
import { useUserDashboard } from '../../hooks/useUserDashboard';
import { AnimatedAmount } from './animated-amount.component';

const InfoIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <circle cx="8" cy="8" r="6.4" stroke="currentColor" strokeWidth="1.3" />
    <path d="M8 7.1V11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    <circle cx="8" cy="5.1" r="0.85" fill="currentColor" />
  </svg>
);

interface StatCardProps {
  title: string;
  children?: React.ReactNode;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  children,
  className = ''
}) => {
  const {
    loading,
    error,
    hasData,
    totalAmount,
    paidAmount,
    overdueAmount,
    paidPercentage,
    overduePercentage,
    fetchSummary
  } = useInvoiceSummary();
  const {
    dashboardData,
  } = useUserDashboard();
  const { t } = useTranslation();

  // Show exactly 2 decimals for the receivables amounts (same locale as the
  // hook's formatAmount, but pinned to 2 fraction digits). Scoped here so the
  // shared hook formatter used elsewhere is unaffected.
  const isSwedish = new URLSearchParams(window.location.search).get('lang') === 'sv-se';
  const formatAmount2 = (num: number) =>
    new Intl.NumberFormat(isSwedish ? 'sv-SE' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);

  // Fetch summary data on component mount
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Loading state
  if (loading) {
    return (
      <div className={`stat-card ${className}`}>
        <div className="stat-card-header">
          <h3 className="stat-card-title">{title}</h3>
          {children}
        </div>
        <div className="stat-card-body d-flex justify-content-between align-items-center">
          <div className="loading-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#666'
          }}>
            Loading summary...
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`stat-card ${className}`}>
        <div className="stat-card-header">
          <h3 className="stat-card-title">{title}</h3>
          {children}
        </div>
        <div className="stat-card-body">
          <div className="error-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#dc3545'
          }}>
            Error loading summary: {error}
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!hasData) {
    return (
      <div className={`stat-card ${className}`}>
        <div className="stat-card-header">
          <h3 className="stat-card-title">{title}</h3>
          {children}
        </div>
        <div className="stat-card-body">
          <div className="empty-state" style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#666'
          }}>
            No summary data available.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`stat-card ${className}`}>
      <div className="stat-card-header">
        <div>

          <h3 className="stat-card-title">
            {title}
            <Tooltip
              title={t('NO_RECEIVABLES_MESSAGE')}
              arrow
              placement="top"
            >
              <span className="stat-card-info" tabIndex={0} aria-label={title}>
                <InfoIcon />
              </span>
            </Tooltip>
          </h3>
         {Number(dashboardData?.response?.length) === 0 && <p className='d-none d-md-block'>{t('NO_RECEIVABLES_MESSAGE')}</p>}
        </div>
        {children}

      </div>

      <div className="stat-card-body flex-wrap">
        <div className="stat-total-section mb-0">
          <p className="stat-total-label">
            <span className="stat-total-caption">{t('TOTAL_INVOICES')}</span>
            <span className="stat-total-amount ds-gradient-text">
              <AnimatedAmount value={totalAmount} format={formatAmount2} /> {t('kr')}
            </span>
          </p>
          <Tooltip
            title={`${t('PAID')} ${paidPercentage.toFixed(0)}% · ${t('OVERDUE')} ${overduePercentage.toFixed(0)}%`}
            arrow
            placement="top"
          >
            <div className="stat-progress-bar">
              {(paidPercentage > 0 || overduePercentage > 0) ? (
                <>
                  <div
                    className="stat-progress-paid"
                    style={{ width: `${paidPercentage}%` }}
                  ></div>
                  <div
                    className="stat-progress-overdue"
                    style={{ width: `${overduePercentage}%` }}
                  ></div>
                </>
              ) : (
                <div
                  className="stat-progress-empty"
                  style={{ width: '100%' }}
                ></div>
              )}
            </div>
          </Tooltip>
        </div>

        <div className="stat-values-section">
          <div className="stat-divider d-md-block d-none"></div>
          <div className="stat-value-item">
            <p className="stat-value-label paid"><span className="stat-value-dot" />{t('PAID')}</p>
            <p className="stat-value-amount"><AnimatedAmount value={paidAmount} format={formatAmount2} /> { t('kr')}</p>
          </div>

          {/* <div className="stat-divider"></div>
          <div className="stat-value-item">
            <p className="stat-value-label overdue">{t('OVERDUE')}</p>
            <p className="stat-value-amount">{formatAmount(overdueAmount)}</p>
          </div> */}
        </div>
      </div>
    </div>
  );
};
